import { useId } from 'react';

const SIZE_MAP = {
  xs: 'w-4 h-4',
  sm: 'w-6 h-6',
  md: 'w-7 h-7',
  lg: 'w-8 h-8',
  xl: 'w-9 h-9',
};

function hasExplicitBoxSize(className) {
  return /\b(?:w|h|size)-/.test(String(className || ''));
}

/**
 * Twinnee mark — metallic clapperboard (drop-in for Lucide Sparkles / AI sparkle icons).
 * Pass Lucide-style size classes (`className="w-3.5 h-3.5"`) or a `size` preset.
 */
export function TwinneeLogo({
  size = 'sm',
  className = '',
  title = 'Twinnee',
  // Lucide-compat props (ignored)
  strokeWidth: _strokeWidth,
  absoluteStrokeWidth: _absoluteStrokeWidth,
  ...rest
}) {
  const uid = useId().replace(/:/g, '');
  const metalId = `twinnee-metal-${uid}`;
  const shineId = `twinnee-shine-${uid}`;
  const goldId = `twinnee-gold-${uid}`;
  const box = hasExplicitBoxSize(className) ? '' : (SIZE_MAP[size] || SIZE_MAP.sm);

  return (
    <svg
      viewBox="0 0 24 24"
      className={`inline-block shrink-0 ${box} ${className}`.trim()}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      role="img"
      aria-label={title}
      {...rest}
    >
      <defs>
        <linearGradient id={metalId} x1="3" y1="10" x2="21" y2="22" gradientUnits="userSpaceOnUse">
          <stop offset="0%" stopColor="#f4f4f5" />
          <stop offset="28%" stopColor="#d4d4d8" />
          <stop offset="52%" stopColor="#fafafa" />
          <stop offset="76%" stopColor="#71717a" />
          <stop offset="100%" stopColor="#3f3f46" />
        </linearGradient>
        <linearGradient id={shineId} x1="12" y1="11" x2="12" y2="20" gradientUnits="userSpaceOnUse">
          <stop offset="0%" stopColor="#ffffff" stopOpacity="0.45" />
          <stop offset="100%" stopColor="#ffffff" stopOpacity="0" />
        </linearGradient>
        <linearGradient id={goldId} x1="2" y1="2" x2="22" y2="11" gradientUnits="userSpaceOnUse">
          <stop offset="0%" stopColor="#fef3c7" />
          <stop offset="35%" stopColor="#fde68a" />
          <stop offset="65%" stopColor="#fbbf24" />
          <stop offset="100%" stopColor="#b45309" />
        </linearGradient>
      </defs>
      <path
        d="M4.5 11.25h15v8.25H4.5z"
        fill={`url(#${metalId})`}
        stroke="#27272a"
        strokeWidth="0.65"
        strokeLinejoin="round"
      />
      <path
        d="M20.2 6 3 11l-.9-4.4"
        fill={`url(#${goldId})`}
        stroke="#78350f"
        strokeWidth="0.75"
        strokeLinejoin="round"
      />
      <path d="m6.2 5.4 8.9-2.8" stroke="#92400e" strokeWidth="0.65" strokeLinecap="round" />
      <path d="M7.5 11.25v8.25M11 11.25v8.25M14.5 11.25v8.25M18 11.25v8.25" stroke="#52525b" strokeWidth="0.55" opacity="0.55" />
      <path d="M5 19.5h14" stroke="#18181b" strokeWidth="0.75" strokeLinecap="round" />
      <path d="M4.5 11.25h15v8.25H4.5z" fill={`url(#${shineId})`} />
      {/* Brand sparkles baked into the mark (not the Lucide Sparkles icon). */}
      <path
        d="M18.2 3.2 18.7 4.4 19.9 4.9 18.7 5.4 18.2 6.6 17.7 5.4 16.5 4.9 17.7 4.4Z"
        fill="#fde68a"
      />
      <path
        d="M14.6 2.4 14.85 3 15.45 3.25 14.85 3.5 14.6 4.1 14.35 3.5 13.75 3.25 14.35 3Z"
        fill="#fbbf24"
      />
    </svg>
  );
}

export default TwinneeLogo;
